import React, { useEffect } from "react";
import { CircularProgress, ThemeProvider, createTheme, ThemeOptions } from "@mui/material";
import type { Theme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setLoading, setThemeConfig } from "@store/slices/themeSlice";
import type { RootState } from "@store/store";
import { useLazyGetThemeConfigQuery } from "@api/catalogs";
import { AppThemePalette } from "@interfaces/theme";
import { themeBase } from "@theme/_themeBase";
import { getBaseUrl } from "@helpers/funciones";

const defaultTheme: AppThemePalette = {
  primary: {
    main: "#528CD6",
    dark: "#132B52",
    light: "#A3D4E8",
    base: "#E3F2F8",
    accent: "#F2704F",
  },
  components: {},
};

const createDynamicTheme = (paletteConfig: AppThemePalette) => {
  const themeOptions: ThemeOptions = {
    palette: {
      primary: {
        main: paletteConfig.primary.main,
        dark: paletteConfig.primary.dark,
        light: paletteConfig.primary.light,
        base: paletteConfig.primary.base,
        accent: paletteConfig.primary.accent,
      },
      components: {
        slider: paletteConfig.components?.slider,
        progressBar: paletteConfig.components?.progressBar,
        buttonSelect: paletteConfig.components?.buttonSelect,
      },
    },
  };
  return createTheme(themeBase, themeOptions);
};

interface DynamicThemeProviderProps {
  children: React.ReactNode;
}

export default function DynamicThemeProvider({ children }: DynamicThemeProviderProps) {
  const dispatch = useDispatch();

  const { loading } = useSelector((state: RootState) => state.theme);

  const [getThemeConfig] = useLazyGetThemeConfigQuery();
  const [customTheme, setCustomTheme] = React.useState<Theme | null>(null);

  const fetchData = React.useCallback(async () => {
    try {
      dispatch(setLoading(true));

      let baseUrl = getBaseUrl(window.location.href);
      const baseurlEnv = import.meta.env.VITE_LOCALHOST_THEME_URL;

      if (baseUrl.includes("localhost") && baseurlEnv) {
        baseUrl = baseurlEnv;
      }

      const response = await getThemeConfig(baseUrl).unwrap();
      dispatch(
        setThemeConfig({
          palette: response.palette.palette,
          interface: response.palette.interface,
        }),
      );
      setCustomTheme(createDynamicTheme(response.palette.palette));
    } catch (error) {
      console.error("Error fetching theme config", error);
      setCustomTheme(createDynamicTheme(defaultTheme));
    } finally {
      dispatch(setLoading(false));
    }
  }, [getThemeConfig, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading || !customTheme) {
    return <CircularProgress size={40} />;
  }

  return <ThemeProvider theme={customTheme}>{children}</ThemeProvider>;
}
